/** Linear scale helpers and SVG path builders for Cybrbase charts */

export function clamp(n, min, max) {
  return Math.min(max, Math.max(min, n));
}

export function linearScale(domainMin, domainMax, rangeMin, rangeMax) {
  const d = domainMax - domainMin || 1;
  const r = rangeMax - rangeMin;
  return (value) => rangeMin + ((Number(value) - domainMin) / d) * r;
}

export function niceTicks(min, max, count = 6) {
  const step = (max - min) / Math.max(1, count - 1);
  return Array.from({ length: count }, (_, i) => min + step * i);
}

/**
 * Compute a "nice" axis upper bound and evenly spaced ticks from a data max.
 * Auto-scales to the data instead of a fixed range, so small values (e.g. a
 * 0–1 MIL range) fill the axis with decimal ticks rather than being squashed
 * against a hardcoded larger max. Returns { max, ticks, decimals }.
 */
export function niceAxis(dataMax, tickCount = 5) {
  const max = Number(dataMax) || 0;
  if (max <= 0) {
    return { max: 1, ticks: [0, 0.25, 0.5, 0.75, 1], decimals: 2 };
  }
  const rough = max / tickCount;
  const pow = Math.pow(10, Math.floor(Math.log10(rough)));
  let step = pow * 10;
  for (const c of [1, 2, 2.5, 5, 10]) {
    if (pow * c >= rough) {
      step = pow * c;
      break;
    }
  }
  const niceMax = Math.ceil(max / step) * step;
  const count = Math.round(niceMax / step);
  const decimals = step < 1 ? (step < 0.1 ? 2 : 1) : 0;
  const ticks = Array.from(
    { length: count + 1 },
    (_, i) => Math.round(i * step * 1000) / 1000
  );
  return { max: niceMax, ticks, decimals };
}

/** Format a numeric axis tick to the given decimal places, trimming noise. */
export function formatTick(value, decimals = 0) {
  return Number(value).toFixed(decimals);
}

/** Catmull-Rom → cubic Bezier smooth line through points */
export function smoothLinePath(points) {
  if (!points?.length) return "";
  if (points.length === 1) return `M ${points[0].x} ${points[0].y}`;

  let d = `M ${points[0].x} ${points[0].y}`;
  for (let i = 0; i < points.length - 1; i++) {
    const p0 = points[i - 1] || points[i];
    const p1 = points[i];
    const p2 = points[i + 1];
    const p3 = points[i + 2] || p2;
    const cp1x = p1.x + (p2.x - p0.x) / 6;
    const cp1y = p1.y + (p2.y - p0.y) / 6;
    const cp2x = p2.x - (p3.x - p1.x) / 6;
    const cp2y = p2.y - (p3.y - p1.y) / 6;
    d += ` C ${cp1x} ${cp1y}, ${cp2x} ${cp2y}, ${p2.x} ${p2.y}`;
  }
  return d;
}

export function areaPath(points, baselineY) {
  if (!points?.length) return "";
  const line = smoothLinePath(points);
  const first = points[0];
  const last = points[points.length - 1];
  return `${line} L ${last.x} ${baselineY} L ${first.x} ${baselineY} Z`;
}

/** Straight (polyline) path — no curve overshoot, so runs of equal/zero values
 *  stay flat instead of dipping below the axis like a smoothed curve does. */
export function polylinePath(points) {
  if (!points?.length) return "";
  return points.map((p, i) => `${i === 0 ? "M" : "L"} ${p.x} ${p.y}`).join(" ");
}

export function polylineAreaPath(points, baselineY) {
  if (!points?.length) return "";
  const first = points[0];
  const last = points[points.length - 1];
  return `${polylinePath(points)} L ${last.x} ${baselineY} L ${first.x} ${baselineY} Z`;
}

export function polarPoint(cx, cy, radius, angleRad) {
  return {
    x: cx + radius * Math.sin(angleRad),
    y: cy - radius * Math.cos(angleRad),
  };
}

export function radarPolygon(values, cx, cy, maxR, maxValue = 100) {
  const n = values.length;
  if (!n) return { points: [], path: "" };
  const points = values.map((v, i) => {
    const angle = (Math.PI * 2 * i) / n;
    const r = maxR * clamp(Number(v) / maxValue, 0, 1);
    return polarPoint(cx, cy, r, angle);
  });
  const path =
    points.map((p, i) => `${i === 0 ? "M" : "L"} ${p.x} ${p.y}`).join(" ") +
    " Z";
  return { points, path };
}

export function truncateLabel(text, max = 18) {
  const s = String(text || "");
  if (s.length <= max) return s;
  return `${s.slice(0, max - 1)}…`;
}

/**
 * Wrap a label into multiple lines by word, so long axis labels can render
 * in full (horizontal, stacked) instead of being truncated with an ellipsis.
 * Returns an array of line strings.
 */
export function wrapLabel(text, maxChars = 14) {
  const words = String(text || "")
    .split(/\s+/)
    .filter(Boolean);
  const lines = [];
  let line = "";
  for (const w of words) {
    if (!line) line = w;
    else if ((line + " " + w).length <= maxChars) line += " " + w;
    else {
      lines.push(line);
      line = w;
    }
  }
  if (line) lines.push(line);
  return lines;
}
